import { useCallback, useContext, useEffect, useState } from 'react';
import { CONVERSATION_CONSTANTS } from '../../config/miaw/constants';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import { IApiHooksWithTrigger } from '../../interfaces/app-interfaces';
import { closeConversation } from '../../utils/miaw/api-utils';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { CAContext } from '../../contexts/CAContext';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import { clearInMemoryData } from '../../utils/miaw/chat-config';
import { useMIAWAgentActions } from './useMIAWAgentActions';
import {
  clearChatbotToken,
  clearChatSessionType,
  clearIsUserLoggedInAtChatStart,
  clearPreChatInfoFromLocalStorage,
  clearRestId,
} from '../../utils/session-storage-utils';

const useMIAWChatEnd = (): IApiHooksWithTrigger<string> => {
  const { logCustomAttributesNR } = useNRLogging();
  const {
    state: { conversationId },
    setState: setMIAWContext,
  } = useContext(MiawChatContext);
  const { setChatState } = useContext(CAContext);
  const { setState: setChatbotState } = useContext(ChatbotContext);
  const [data, setData] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  const [toggle, setToggle] = useState<boolean>(false);

  const { handleCloseEventSource } = useMIAWAgentActions();

  const trigger = useCallback(() => {
    /** Once chat is closed, remove previous details */
    clearPreChatInfoFromLocalStorage();
    setToggle((prev) => !prev);
  }, []);

  useEffect(() => {
    if (toggle) {
      setLoading(true);
      logCustomAttributesNR('ca-chatbot-miaw-api-request', {
        logLevel: 'info',
        message: 'Closing conversation',
        conversationId,
        apiType: 'miawCloseConversation',
      });
      console.log('Closing conversation with ID:', conversationId);
      if (conversationId) {
        closeConversation(conversationId)
          .then((res) => {
            console.log('Conversation closed successfully', res);
            setData(`Conversation CLosed ${conversationId}`);
            setError(undefined);
            setMIAWContext((prev) => ({ ...prev, conversationStatus: CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION }));
            logCustomAttributesNR('ca-chatbot-miaw-api-response', {
              logLevel: 'info',
              message: 'Conversation closed successfully',
              conversationId,
              apiType: 'miawCloseConversation',
            });
          })
          .catch((error) => {
            console.error(error);
            setError(error as Error);
            if (error instanceof Response) {
              error
                .text()
                .then((errorMessage: string) => {
                  const err = new Error(errorMessage);
                  logCustomAttributesNR('ca-chatbot-miaw-api-error', {
                    errorName: err.name,
                    errorStack: err.stack,
                    message: err.message,
                    logLevel: 'error',
                    apiType: 'miawCloseConversation',
                  });
                })
                .catch((textError) => {
                  console.error(textError);
                });
            } else {
              logCustomAttributesNR('ca-chatbot-miaw-api-error', {
                errorName: error.name,
                errorStack: error.stack,
                message: error.message,
                logLevel: 'error',
                apiType: 'miawCloseConversation',
              });
            }
          })
          .finally(() => {
            setLoading(false);
            setToggle(false);
            handleCloseEventSource();
            setMIAWContext((prevState) => ({
              ...prevState,
              conversationMessages: [],
              conversationId: '',
              agentName: '',
              conversationStatus: CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION,
            }));
            clearInMemoryData();

            setChatbotState((prevState) => ({
              ...prevState,
              conversationMessages: [],
              discussionId: '',
              sessionId: '',
            }));
            clearIsUserLoggedInAtChatStart();
            clearChatbotToken();
            clearRestId();
            clearChatSessionType();
            setChatState((prevState) => ({
              ...prevState,
              // chatWindow: 'minimized',
              chatSession: 'endChatSession',
              isPrivacyPolicyAccepted: false,
              unreadMessages: 0,
            }));
          });
      } else {
        clearChatbotToken();
        clearChatSessionType();
        console.log('No conversationId found');
        setLoading(false);
        setError(new Error('No conversationId found'));
        setData(undefined);
        setChatState((prevState) => ({
          ...prevState,
          // chatWindow: 'minimized',
          chatSession: 'endChatSession',
          isPrivacyPolicyAccepted: false,
          unreadMessages: 0,
        }));
      }
    }
  }, [toggle, conversationId]);

  return {
    loading,
    trigger,
    data,
    error,
  };
};

export { useMIAWChatEnd };
